import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api, { formatError } from "../../lib/api";
import { levelMeta } from "../../lib/scoring";
import { toast } from "sonner";
import {
  UserPlus, Search, QrCode, CalendarPlus, Eye, X, Loader2, Upload, CheckSquare, Square, Users, Mail,
} from "lucide-react";

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-2xl border border-slate-200 shadow-card ${className}`}>{children}</div>
);

function AddStudentModal({ roles, onClose, onAdded }) {
  const [f, setF] = useState({ name: "", email: "", branch: "Computer Science", graduation_year: "2026", target_role: "software-engineer" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.name || !f.email) return toast.error("Name and email are required");
    setBusy(true);
    try { await api.post("/college/students", f); toast.success("Student added"); onAdded(); onClose(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Add Student" onClose={onClose}>
      <div className="space-y-3">
        <input data-testid="student-name" placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
        <input data-testid="student-email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
        <div className="grid grid-cols-2 gap-3">
          <input placeholder="Branch" value={f.branch} onChange={(e) => setF({ ...f, branch: e.target.value })} className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
          <input placeholder="Grad year" value={f.graduation_year} onChange={(e) => setF({ ...f, graduation_year: e.target.value })} className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand" />
        </div>
        <select data-testid="student-role" value={f.target_role} onChange={(e) => setF({ ...f, target_role: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand">
          {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
        </select>
      </div>
      <button data-testid="save-student" onClick={save} disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Add Student"}</button>
    </Modal>
  );
}

function BulkImportModal({ onClose, onDone }) {
  const [text, setText] = useState("name,email,branch,graduation_year,target_role\nJohn Doe,john@abc.edu,Computer Science,2026,Software Engineer");
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    setBusy(true);
    try { const { data } = await api.post("/college/students/bulk", { csv: text }); toast.success(`${data.added} added, ${data.skipped} skipped`); onDone(); onClose(); }
    catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title="Bulk Import Students (CSV)" onClose={onClose}>
      <p className="text-xs text-ink-soft mb-2">Paste CSV with headers: <span className="num font-semibold">name, email, branch, graduation_year, target_role</span></p>
      <textarea data-testid="bulk-csv" value={text} onChange={(e) => setText(e.target.value)} rows={7} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-xs num focus:outline-none focus:ring-2 focus:ring-brand" />
      <button data-testid="bulk-upload-submit" onClick={upload} disabled={busy} className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <>Upload Students</>}</button>
    </Modal>
  );
}

function BulkScheduleModal({ ids, slots, onClose, onDone }) {
  const [f, setF] = useState({ type: "ai", date: slots.dates?.[0] || "", time: slots.slots?.[0] || "" });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!f.date || !f.time) return toast.error("Pick date & time");
    setBusy(true);
    try {
      const { data } = await api.post("/college/bulk-sponsor", { student_ids: ids, type: f.type, panel_id: "technical-interviewer", date: f.date, time: f.time, duration: 45 });
      toast.success(`${data.created} interviews scheduled · ${data.credits_remaining} credits left`);
      onDone(); onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Schedule Interview for ${ids.length} Students`} onClose={onClose}>
      <div className="space-y-3">
        <select data-testid="bulk-type" value={f.type} onChange={(e) => setF({ ...f, type: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand"><option value="ai">AI Interview (1 credit each)</option><option value="expert">Expert Interview (2 credits each)</option></select>
        <div className="grid grid-cols-2 gap-3">
          <select data-testid="bulk-date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand">{slots.dates?.map((d) => <option key={d} value={d}>{d}</option>)}</select>
          <select data-testid="bulk-time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} className="rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm num focus:outline-none focus:ring-2 focus:ring-brand">{slots.slots?.map((t) => <option key={t} value={t}>{t}</option>)}</select>
        </div>
      </div>
      <button data-testid="bulk-schedule-submit" onClick={submit} disabled={busy} className="mt-5 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm & Deduct Credits"}</button>
    </Modal>
  );
}

function Modal({ title, children, onClose }) {
  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4">
      <div className="absolute inset-0 bg-ink/40" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-lift w-full max-w-md p-6">
        <div className="flex items-center justify-between mb-4"><div className="font-heading font-bold text-lg text-ink">{title}</div><button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="h-4 w-4" /></button></div>
        {children}
      </div>
    </div>
  );
}

function ShareEmailModal({ ids, onClose }) {
  const [f, setF] = useState({ to: "", message: "" });
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/college/email-students", { student_ids: ids, to: f.to || null, message: f.message || null });
      toast.success(data.note || `Shared ${data.count} candidates to ${data.to}`);
      onClose();
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <Modal title={`Share ${ids.length} Candidates via Email`} onClose={onClose}>
      <p className="text-xs text-ink-soft mb-3">Each candidate's verified QR scorecard link is included. Leave email blank to send to yourself.</p>
      <div className="space-y-3">
        <input data-testid="share-email-to" placeholder="Recipient email (optional)" value={f.to} onChange={(e) => setF({ ...f, to: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
        <textarea data-testid="share-email-message" placeholder="Add a note (optional)" rows={3} value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} className="w-full rounded-lg border border-slate-300 px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
      </div>
      <button data-testid="share-email-submit" onClick={send} disabled={busy} className="mt-4 w-full inline-flex items-center justify-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-3 rounded-full disabled:opacity-60 transition-colors">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Mail className="h-4 w-4" /> Send Email</>}</button>
    </Modal>
  );
}

export default function Students() {
  const navigate = useNavigate();
  const [rows, setRows] = useState(null);
  const [roles, setRoles] = useState([]);
  const [slots, setSlots] = useState({});
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState("all");
  const [modal, setModal] = useState(null);
  const [selected, setSelected] = useState(new Set());

  const load = () => api.get("/college/students").then((r) => setRows(r.data));
  useEffect(() => { load(); api.get("/catalog/job-roles").then((r) => setRoles(r.data)); api.get("/catalog/slots").then((r) => setSlots(r.data)); }, []);

  if (!rows) return <div className="text-ink-soft">Loading…</div>;

  const filtered = rows.filter((s) => {
    const mq = `${s.name} ${s.email} ${s.student_code} ${s.target_role}`.toLowerCase().includes(q.toLowerCase());
    const mf = filter === "all" || (filter === "completed" && s.status === "Completed") || (filter === "pending" && s.status !== "Completed");
    return mq && mf;
  });

  const toggle = (id) => { const n = new Set(selected); n.has(id) ? n.delete(id) : n.add(id); setSelected(n); };
  const allSel = filtered.length && filtered.every((s) => selected.has(s.id));
  const toggleAll = () => { if (allSel) setSelected(new Set()); else setSelected(new Set(filtered.map((s) => s.id))); };

  return (
    <div className="space-y-6">
      {modal === "add" && <AddStudentModal roles={roles} onClose={() => setModal(null)} onAdded={load} />}
      {modal === "bulk" && <BulkImportModal onClose={() => setModal(null)} onDone={load} />}
      {modal === "schedule" && <BulkScheduleModal ids={[...selected]} slots={slots} onClose={() => setModal(null)} onDone={() => { setSelected(new Set()); load(); }} />}
      {modal === "share" && <ShareEmailModal ids={[...selected]} onClose={() => setModal(null)} />}

      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight text-ink">Students</h1>
          <p className="text-ink-soft text-sm mt-1">{rows.length} students in your college</p>
        </div>
        <div className="flex gap-2">
          <button data-testid="bulk-import" onClick={() => setModal("bulk")} className="inline-flex items-center gap-2 text-sm font-bold text-ink bg-slate-100 hover:bg-slate-200 px-4 py-2.5 rounded-full transition-colors"><Upload className="h-4 w-4" /> Bulk Import</button>
          <button data-testid="add-student" onClick={() => setModal("add")} className="inline-flex items-center gap-2 text-sm font-bold text-white bg-brand hover:bg-brand-hover px-5 py-2.5 rounded-full shadow-ambient transition-colors"><UserPlus className="h-4 w-4" /> Add Student</button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-ink-faint" />
          <input data-testid="student-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search students…" className="w-full rounded-full border border-slate-300 pl-10 pr-4 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand" />
        </div>
        <div className="flex gap-1 bg-slate-100 rounded-full p-1">
          {["all", "completed", "pending"].map((f) => (
            <button key={f} data-testid={`filter-${f}`} onClick={() => setFilter(f)} className={`text-xs font-bold px-4 py-2 rounded-full capitalize transition-colors ${filter === f ? "bg-white text-ink shadow-sm" : "text-ink-soft"}`}>{f}</button>
          ))}
        </div>
      </div>

      {selected.size > 0 && (
        <div className="flex items-center justify-between rounded-xl bg-brand-light border border-brand/20 px-4 py-3" data-testid="bulk-action-bar">
          <span className="text-sm font-semibold text-ink flex items-center gap-2"><Users className="h-4 w-4 text-brand" /> {selected.size} selected</span>
          <div className="flex gap-2">
            <button onClick={() => setSelected(new Set())} className="text-xs font-bold text-ink-soft px-3 py-1.5">Clear</button>
            <button data-testid="bulk-share" onClick={() => setModal("share")} className="inline-flex items-center gap-1.5 text-xs font-bold text-ink bg-white border border-slate-300 hover:border-slate-400 px-4 py-2 rounded-full transition-colors"><Mail className="h-3.5 w-3.5" /> Share via Email</button>
            <button data-testid="bulk-schedule" onClick={() => setModal("schedule")} className="inline-flex items-center gap-1.5 text-xs font-bold text-white bg-brand hover:bg-brand-hover px-4 py-2 rounded-full transition-colors"><CalendarPlus className="h-3.5 w-3.5" /> Schedule Interview</button>
          </div>
        </div>
      )}

      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs font-bold uppercase tracking-wider text-ink-faint border-b border-slate-200">
                <th className="px-4 py-3"><button onClick={toggleAll} data-testid="select-all">{allSel ? <CheckSquare className="h-4 w-4 text-brand" /> : <Square className="h-4 w-4 text-ink-faint" />}</button></th>
                <th className="px-4 py-3">Student ID</th><th className="px-4 py-3">Name</th><th className="px-4 py-3">Branch</th>
                <th className="px-4 py-3">Grad Yr</th><th className="px-4 py-3">Target Role</th><th className="px-4 py-3">Readiness</th>
                <th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map((s) => {
                const meta = levelMeta(s.readiness);
                return (
                  <tr key={s.id} className="hover:bg-slate-50 transition-colors" data-testid={`student-row-${s.id}`}>
                    <td className="px-4 py-3.5"><button onClick={() => toggle(s.id)} data-testid={`select-${s.id}`}>{selected.has(s.id) ? <CheckSquare className="h-4 w-4 text-brand" /> : <Square className="h-4 w-4 text-ink-faint" />}</button></td>
                    <td className="px-4 py-3.5 num text-ink-soft">{s.student_code || "—"}</td>
                    <td className="px-4 py-3.5 font-semibold text-ink">{s.name}<div className="text-xs text-ink-faint font-normal">{s.email}</div></td>
                    <td className="px-4 py-3.5 text-ink-soft">{s.branch}</td>
                    <td className="px-4 py-3.5 num text-ink-soft">{s.graduation_year}</td>
                    <td className="px-4 py-3.5 text-ink-soft">{s.target_role}</td>
                    <td className="px-4 py-3.5 num font-bold text-ink">{s.readiness ?? "—"}</td>
                    <td className="px-4 py-3.5"><span className="text-xs font-bold px-2.5 py-1 rounded-full" style={{ background: meta.bg, color: meta.text }}>{s.readiness_level}</span></td>
                    <td className="px-4 py-3.5">
                      <div className="flex items-center justify-end gap-1">
                        <button onClick={() => navigate(`/college/student/${s.id}`)} data-testid={`view-${s.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-ink-soft" title="View Progression"><Eye className="h-4 w-4" /></button>
                        <button onClick={() => navigate(`/college/sponsor?student=${s.id}`)} data-testid={`schedule-${s.id}`} className="p-2 rounded-lg hover:bg-slate-100 text-ink-soft" title="Schedule Interview"><CalendarPlus className="h-4 w-4" /></button>
                        {s.verification_id ? <Link to={`/scorecard/${s.verification_id}`} target="_blank" className="p-2 rounded-lg hover:bg-slate-100 text-ink-soft" title="QR Scorecard"><QrCode className="h-4 w-4" /></Link> : <span className="p-2 opacity-30"><QrCode className="h-4 w-4" /></span>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!filtered.length && <tr><td colSpan={9} className="px-5 py-10 text-center text-ink-soft">No students found.</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
